import "../../css/App.css";
import "../../css/FullEvent/FullEvent.css";
import "../../css/FullEvent/FullEventInputForm.css";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Event,
  CustomTicketV2,
  TicketV2,
  FollowSourceType,
  SavedFormQuestion,
  FormAnswersV2,
  PromoCode,
  VerificationState,
  AccountData,
  NotificationType,
  TicketType,
} from "@markit/common.types";
import { Theme, createTheme } from "@mui/material/styles";
import {
  createEmptyFormAnswersV2,
  isCustomTickets,
  isEventTicketsPaid,
  getVisibleCustomTickets,
  isPaidTicketType,
  MARKIT_CONTACT_US,
  isRequestTickets,
  isProductSpecificType,
  productItemLabel,
  mostRecentSubscriptionPlan,
} from "@markit/common.utils";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import CheckoutForm from "../FullEventSubComponents/CheckoutForm";
import { useFullEventPrices } from "../../utils/useFullEventPrices";
import { API } from "../../API";
import { FormQuestionsInput } from "../UserInfoContainers/FormQuestionsInput";
import { FullEventTicketOptions } from "./FullEventTicketOptions";
import { FullEventPayment } from "./FullEventPayment";
import { userNumberOfTicketsInGroup } from "../../utils/eventUtils/userTicketUtils";
import {
  getTicketDescription,
  getTicketsRemainingInGroup,
  ticketInStock,
  isUserOrganizer,
} from "../../utils/eventUtils/eventUtils";
import {
  hasInvalidFlexiblePriceInput,
  countPaidTickets,
  isTotalBelowStripeMinimum,
  getEffectivePriceCents,
} from "../../utils/eventUtils/customTicketUtils";
import { MixpanelContext } from "../../context/AnalyticsService";
import { isDesktop } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { DataLoaders } from "../../redux/slices/dataSlice";
import { Colors } from "../../utils/colors";
import { paymentTimeoutHandler } from "../../utils/FullEventUtils/inputFormUtils";
import { useTheme } from "../../hooks/useTheme";
import {
  getEventCustomTicketFormQuestions,
  getUsersFormResponses,
  validateEventForm,
  finalizeFormFileUploadAnswers,
} from "../../utils/eventUtils/formUtils";
import { NameAndPhoneNumberInput } from "../UserInfoContainers/NameAndPhoneNumberInput";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { getDocs, onSnapshot, query, where } from "../../firebase";
import { getTicketsRef } from "../../utils/FirebaseUtils";
import { useNavigate } from "../../hooks/useNavigate";
import { FullEventExistingTicketInfo } from "./FullEventExistingTicketInfo";
import {
  checkIfLoggedInPhoneNumber,
  checkIfUserIsUnsubscribed,
  checkIfUserIsUnsubscribedMarkit,
} from "../../utils/userUtils";
import useOnUnmount from "../../hooks/useOnUnmount";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { isValidUSPhoneNumber } from "../../utils/FormatPhoneNumber";
import ExpandableBox from "../Containers/ExpandableBox";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { FullEventPaymentSummary } from "./FullEventPaymentSummary";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { useWindowResize } from "../../hooks/useWindowResize";
import { useOnMount } from "../../hooks/useOnMount";
import { getStripePromise, testStripe } from "../../utils/stripeAccountUtils";
import { FormResponsesProvider } from "../Form/FormResponsesContext";

export const themeMui: Theme = createTheme({
  palette: {
    primary: { main: Colors.BLUE1, contrastText: "#fff" },
    secondary: {
      main: Colors.BLUE5,
    },
  },
  typography: {
    button: {
      textTransform: "none",
    },
  },
});

type FullEventInputFormProps = {
  event: Event;
  host: AccountData;
  tixId: string;
  ticketData?: TicketV2;
  ticketToRedeem: CustomTicketV2 | undefined;
  promoCode?: PromoCode;
  promoterId: string;
  userReferrer: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedTickets: CustomTicketV2[]) => void;
  isEventAtCapacity: boolean;
  eventFormQuestions: SavedFormQuestion[];
};

/** Modal Form that is showed on the browser when the user wants to view the tickets **/
export const FullEventInputForm = memo(function FullEventInputFormFn(
  props: FullEventInputFormProps
) {
  const {
    event,
    host,
    tixId,
    ticketData,
    ticketToRedeem,
    promoCode,
    promoterId,
    userReferrer,
    selectedCustomTickets,
    setSelectedCustomTickets,
    isEventAtCapacity,
    eventFormQuestions,
  } = props;
  const { account } = useSelector(getAccountState);
  const { linkTrackerId, loggedIn, accountData } = account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [answers, setAnswers] = useState<FormAnswersV2>(
    createEmptyFormAnswersV2(eventFormQuestions, [])
  );
  const answersRef = useRef(answers);
  const unsubscribeRef = useRef<(() => void) | void>();
  const mixpanel = useContext(MixpanelContext);

  const [formFileUploads, setFormFileUploads] = useState<
    Record<string, string>
  >({}); // original file name per file-upload question (URL lives in answers)
  const [formErrorIds, setFormErrorIds] = useState<string[]>([]);
  const [loadingFilledResponses, setLoadingFilledResponses] = useState(false);
  const [uid, setUid] = useState("");
  const [chargeId, setChargeId] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [options, setOptions] = useState<{
    clientSecret: string;
    appearance: {};
  }>();
  const [processing, setProcessing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentSheetLoading, setPaymentSheetLoading] = useState(false);
  const [verificationState, setVerificationState] = useState(
    VerificationState.UNVERIFIED
  );
  const [optedOut, setOptedOut] = useState(false);
  const [optedOutCreator, setOptedOutCreator] = useState(false);

  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [tempFullName, setTempFullName] = useState("");
  const [ticketQuantities, setTicketQuantities] = useState<Map<string, number>>(
    new Map()
  );
  const [namedPrices, setNamedPrices] = useState<Map<string, number>>(
    new Map()
  ); // Used for pay what you want tickets to hold the input amount by user
  const [tipAmountCents, setTipAmountCents] = useState(0);
  const [numAllTicketsUserInGroups, setNumAllTicketsUserInGroups] = useState<
    number[]
  >([]);
  const [numVisibleTicketsUserInGroups, setNumVisibleTicketsUserInGroups] =
    useState<number[]>([]);
  const [numTicketsRemainingInGroups, setNumTicketsRemainingInGroups] =
    useState<number[]>([]);

  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [ticketsInStock, setTicketsInStock] = useState<boolean[] | null>(null);
  const [firstAvailableTicketReady, setFirstAvailableTicketReady] =
    useState(false);
  const [ticketForRedemption, setTicketForRedemption] = useState<
    CustomTicketV2 | undefined
  >(ticketToRedeem);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [isRedeemFreePromoCode, setIsRedeemFreePromoCode] = useState(false);
  const [isBlacklistedUser, setIsBlacklistedUser] = useState(false);
  const [loadingTicketData, setLoadingTicketData] = useState(false);

  const { theme } = useTheme();

  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    blueBold: { color: Colors.BLUE5, fontWeight: 500, fontSize: 12 },
    leftContainer: { gap: 24, width: isDesktop ? 380 : "100%" },
    rightContainer: {
      padding: 14,
      borderRadius: 20,
      width: 362,
      alignSelf: "flex-start",
      position: "sticky",
      top: 24,
    },
  };

  const isEmailBasedAccount = useMemo(
    () => accountData.email !== "",
    [accountData.email]
  );

  const verified = useMemo(
    () => verificationState === VerificationState.VERIFIED,
    [verificationState]
  );

  const ticketsPaid = useMemo(() => isEventTicketsPaid(event), [event]);

  const isProductCheckout = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const itemLabel = useMemo(
    () => productItemLabel(event.eventType),
    [event.eventType]
  );

  const visibleTickets = useMemo(
    () => getVisibleCustomTickets(event.customTickets, ticketForRedemption?.id),
    [event.customTickets, ticketForRedemption?.id]
  );

  const totalTicketQuantity = useMemo(
    () =>
      Array.from(ticketQuantities.values()).reduce(
        (sum, quantity) => sum + quantity,
        0
      ),
    [ticketQuantities]
  );

  // Map the customTickets.formQuestions for the selected tickets to display (maintain unique)
  const formQuestionsToDisplay: SavedFormQuestion[] = useMemo(
    () =>
      getEventCustomTicketFormQuestions(
        selectedCustomTickets,
        eventFormQuestions
      ),
    [eventFormQuestions, selectedCustomTickets]
  );

  const subscriptionPlan = useMemo(
    () => mostRecentSubscriptionPlan(host, testStripe(host.uid)),
    [host]
  );

  const {
    baseTicketPriceCents,
    discountedTicketPriceCents,
    ticketFeesCents,
    totalAmountCents,
    currPromoCode,
    updatePromoCode,
    currTixId,
    updateTixId,
  } = useFullEventPrices({
    tixId,
    promoCode,
    selectedCustomTickets,
    ticketQuantities,
    namedPrices,
    eventHostId: host.uid,
    eventType: event.eventType,
    creatorPriceId: testStripe(host.uid)
      ? subscriptionPlan.testPriceId
      : subscriptionPlan.priceId,
    tipAmountCents,
  });

  useOnMount(() => {
    // Set the ticket quantity for the initial selected ticket
    setTicketQuantities(
      new Map(
        selectedCustomTickets.map((customTicket) => [
          customTicket.id,
          customTicket.minQuantity ?? 1,
        ])
      )
    );
    // If the initial selected tickets include pay what you want, initialize namedPrices for all of them
    const flexibleTickets = selectedCustomTickets.filter(
      (customTicket) => customTicket.flexiblePricing
    );
    if (flexibleTickets.length > 0) {
      setNamedPrices(new Map(flexibleTickets.map((t) => [t.id, t.price])));
    }
  });

  useOnUnmount((_latestDeps) => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = undefined;
    }
  }, []);

  // Needs to be called again once verification state changes to update values and also check if the user has tickets
  // if it's an existing user that verified their number
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    const userId = uid || accountData.uid;
    // Note: non-US numbers can skip this check
    if (
      (!userId || verificationState !== VerificationState.VERIFIED) &&
      isValidUSPhoneNumber(tempPhoneNumber)
    ) {
      return;
    }
    setLoadingTicketData(true);

    // Get the number of tickets user owns for all ticket types
    const getAllNumberOfTicketsInGroup = async () => {
      const ticketsInGroup = await Promise.all(
        event.customTickets.map(
          async (customTicket) =>
            await userNumberOfTicketsInGroup(event.id, userId, customTicket.id)
        )
      );
      return ticketsInGroup;
    };
    // Get the number of tickets a user owns for only visible ticket types
    const getVisibleNumberOfTicketsInGroup = async () => {
      // waits for user to be verified before setting the userNumTicketsInGroup to anything other than an empty array
      // important to do this to make sure we don't accidentally set it before the user is even set, which can mess up first ticket to set
      const ticketsInGroup = await Promise.all(
        visibleTickets.map(
          async (customTicket) =>
            await userNumberOfTicketsInGroup(event.id, userId, customTicket.id)
        )
      );
      return ticketsInGroup;
    };
    // Get the number of tickets remaining for only visible ticket types
    const getVisibleTicketsRemaining = async () => {
      // Gets the number of tickets the user has for each ticket group
      const remainingInGroup = await Promise.all(
        visibleTickets.map(
          async (customTicket) =>
            await getTicketsRemainingInGroup(customTicket, event)
        )
      );
      return remainingInGroup;
    };
    const [allTicketsInGroup, ticketsInGroup, remainingInGroup] =
      await Promise.all([
        getAllNumberOfTicketsInGroup(),
        getVisibleNumberOfTicketsInGroup(),
        getVisibleTicketsRemaining(),
      ]);
    setNumAllTicketsUserInGroups(allTicketsInGroup);
    setNumVisibleTicketsUserInGroups(ticketsInGroup);
    setNumTicketsRemainingInGroups(remainingInGroup);
    setLoadingTicketData(false);
  }, [
    accountData.uid,
    event,
    uid,
    verificationState,
    visibleTickets,
    tempPhoneNumber,
  ]);

  // set existing phone user and follower data if you're logged in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (checkIfLoggedInPhoneNumber(loggedIn, accountData.phoneNumber)) {
      setTempFullName(accountData.fullName);
      setTempPhoneNumber(accountData.phoneNumber);
      setUid(accountData.uid);
      setVerificationState(VerificationState.VERIFIED);

      setLoadingFilledResponses(true);

      // Check if logged in phone number is unsubscribed from markit number or creator
      // Even if using firebase OTP, we need them opted in so they can get confirmation texts
      const isUnsubscribedMarkit = await checkIfUserIsUnsubscribedMarkit(
        accountData.phoneNumber
      );
      setOptedOut(isUnsubscribedMarkit);

      const isUnsubscribed = await checkIfUserIsUnsubscribed(
        accountData.uid,
        event.createdBy
      );
      setOptedOutCreator(isUnsubscribed);

      const userOrganizer = await isUserOrganizer(event, accountData.uid);
      setIsOrganizer(userOrganizer);

      const { formResponses, fileNameRecords } = await getUsersFormResponses(
        event.createdBy,
        eventFormQuestions,
        accountData.uid
      );
      setAnswers(createEmptyFormAnswersV2(eventFormQuestions, formResponses));
      setFormFileUploads(fileNameRecords);
      setLoadingFilledResponses(false);
    }
  }, [
    accountData.fullName,
    accountData.phoneNumber,
    accountData.uid,
    event,
    eventFormQuestions,
    loggedIn,
    tempPhoneNumber,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (
      tempPhoneNumber !== "" &&
      isValidUSPhoneNumber(tempPhoneNumber) &&
      verificationState === VerificationState.VERIFIED
    ) {
      // check if blacklistUser
      const response = await API.user.fetchBlacklistUser({
        phoneNumber: tempPhoneNumber,
        uid: uid,
      });
      if (response.isBlacklisted) {
        mixpanel.track("Webapp: Blacklist User Attempting RSVP", {
          distinct_id: uid,
          phoneNumber: tempPhoneNumber,
          event_id: event.id,
        });
        setIsBlacklistedUser(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, verificationState]);

  const isDisabledInputSection = useMemo(
    () =>
      verificationState !== VerificationState.VERIFIED ||
      processing ||
      (totalAmountCents !== 0 && options === undefined) ||
      isOrganizer ||
      isEmailBasedAccount ||
      optedOut ||
      optedOutCreator,
    [
      isEmailBasedAccount,
      isOrganizer,
      optedOut,
      optedOutCreator,
      options,
      processing,
      totalAmountCents,
      verificationState,
    ]
  );

  const foundCustomTicketIds = useMemo(
    () =>
      ticketForRedemption
        ? [ticketForRedemption.id]
        : selectedCustomTickets.map((ticket) => ticket.id),
    [selectedCustomTickets, ticketForRedemption]
  );

  // Checks if the price input is invalid for flexible pricing ticket or below stripe minimum,
  // Ignore this case if the user is redeeming a free promo code
  const hasInvalidPriceInput = useMemo(
    () =>
      !isRedeemFreePromoCode &&
      (hasInvalidFlexiblePriceInput(selectedCustomTickets, namedPrices) ||
        isTotalBelowStripeMinimum(discountedTicketPriceCents)),
    [
      isRedeemFreePromoCode,
      selectedCustomTickets,
      namedPrices,
      discountedTicketPriceCents,
    ]
  );

  const contactUsOnPress = () => {
    window.open(MARKIT_CONTACT_US);
  };

  const redirectToConfirmation = useCallback(
    (uid?: string) => {
      if (uid) {
        dispatch(DataLoaders.user(uid));
        if (mixpanel) {
          mixpanel.track("Webapp: Successful RSVP To Event", {
            event_id: event.id,
            event_type: ticketsPaid ? "paid" : "free",
            customTicketIds: foundCustomTicketIds,
            numberOfTickets: totalTicketQuantity,
            customer_phone: tempPhoneNumber,
            amount: totalAmountCents,
          });
        }
      }
      showNotificationBanner(
        dispatch,
        uid !== undefined ? "Success" : "Failure",
        uid !== undefined
          ? NotificationType.AFFIRMATIVE
          : NotificationType.NEGATIVE
      );
      navigate(`/e/${event.id}/i/` + (uid || ""), {
        replace: true,
        state: {
          eventId: event.id,
          userId: uid ?? "",
          unlocked: uid !== undefined, // Checkout just completed, so product media starts unlocked
        },
      });
      window.scrollTo(0, 0);
    },
    [
      dispatch,
      event.id,
      foundCustomTicketIds,
      mixpanel,
      navigate,
      tempPhoneNumber,
      totalTicketQuantity,
      ticketsPaid,
      totalAmountCents,
    ]
  );

  const fetchPaymentSheetParams = useCallback(
    async (amount: number, isLatest: () => boolean) => {
      // if user has already paid or was invited to the event, show them their QR Code
      if (event) {
        if (isLatest()) {
          setPaymentSheetLoading(true);
          // Keep previous options visible (don't setOptions(undefined)) so the
          // payment sheet stays mounted until the new PaymentIntent is ready.
        }
        const numPaidTickets = countPaidTickets(
          selectedCustomTickets,
          ticketQuantities,
          namedPrices
        );
        // MUST MANUALLY CHANGE STRIPE TEST VS ACTUAL
        (testStripe(host.uid) ? API.testPayment : API.payment)
          .payWithStripe({
            ...(uid !== undefined && uid !== "" && { uid: uid }),
            eventId: event.id,
            fullName: tempFullName,
            phoneNumber: tempPhoneNumber,
            amount: amount,
            tipAmount: tipAmountCents,
            selectedTicketIndex: -1,
            numTickets: numPaidTickets,
            promoterId: promoterId,
          })
          .then((response) => {
            const { paymentIntent, chargeId, customer } = response;
            if (isLatest()) {
              setChargeId(chargeId);
              setCustomerId(customer);

              // get stripe info
              const stripeOptions = {
                // passing the client secret obtained in step 2
                clientSecret: paymentIntent,
                // Fully customizable with appearance API.
                appearance: {
                  theme: theme.StripeTheme.theme,
                  variables: {
                    colorPrimary: Colors.BLUE5,
                    colorBackground: theme.TertiaryBG.backgroundColor,
                  },
                },
              };
              setOptions(stripeOptions);
            }
          })
          .catch((e: any) => {
            console.log(e.message);
            alert(
              "An error occurred. Please try again. This page will be refreshed."
            );
            window.location.reload();
            if (mixpanel) {
              mixpanel.track(
                "Webapp: Failed PayWithStripe Initializing Payment",
                {
                  event_id: event.id,
                  event_type: ticketsPaid ? "paid" : "free",
                  customTicketIds: foundCustomTicketIds,
                  numberOfTickets: totalTicketQuantity,
                  customer_phone: tempPhoneNumber,
                  amount: totalAmountCents,
                  error_message: e.message,
                }
              );
            }
          })
          .finally(() => {
            setPaymentSheetLoading(false);
          });
      }
    },
    [
      event,
      selectedCustomTickets,
      ticketQuantities,
      namedPrices,
      host.uid,
      uid,
      tempFullName,
      tempPhoneNumber,
      promoterId,
      theme.StripeTheme.theme,
      theme.TertiaryBG.backgroundColor,
      mixpanel,
      ticketsPaid,
      foundCustomTicketIds,
      totalTicketQuantity,
      totalAmountCents,
      tipAmountCents,
    ]
  );

  // needed so that the answers state is always updated to the most recent by storing as a ref.
  // This was needed because of async issues with the useEffect for apple pay when there were form questions and the state was getting cleared.
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const fetchPaymentSheetParamsRef = useRef(fetchPaymentSheetParams);
  useEffect(() => {
    fetchPaymentSheetParamsRef.current = fetchPaymentSheetParams;
  }, [fetchPaymentSheetParams]);

  // Debounce price/tip so we only create a PaymentIntent after the user has stopped
  // changing ticket/quantity/tip (avoids creating an intent on every single change).
  const [debouncedDiscountedPriceCents, setDebouncedDiscountedPriceCents] =
    useState(0);
  useEffect(() => {
    if (discountedTicketPriceCents === 0) {
      setDebouncedDiscountedPriceCents(0);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedDiscountedPriceCents(discountedTicketPriceCents);
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [discountedTicketPriceCents]);

  const [debouncedTipAmountCents, setDebouncedTipAmountCents] = useState(0);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedTipAmountCents(tipAmountCents);
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [tipAmountCents]);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (_latestDeps, isLatest) => {
      if (
        debouncedDiscountedPriceCents !== 0 &&
        verificationState === VerificationState.VERIFIED &&
        firstAvailableTicketReady
      ) {
        fetchPaymentSheetParamsRef.current(
          debouncedDiscountedPriceCents,
          isLatest
        );
      }
    },
    [
      firstAvailableTicketReady,
      debouncedDiscountedPriceCents,
      debouncedTipAmountCents,
      verificationState,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    const tixInStock = await Promise.all(
      visibleTickets.map(
        async (customTicket) => await ticketInStock(customTicket, event)
      )
    );
    setTicketsInStock(tixInStock);

    // Ensure the single selected ticket (if any) is in stock; otherwise pick the first available ticket.
    if (!firstAvailableTicketReady && numVisibleTicketsUserInGroups.length) {
      const selected = selectedCustomTickets[0];
      let selectedIsValid = false;
      if (selected) {
        const index = visibleTickets.findIndex(
          (visibleTicket) => visibleTicket.id === selected.id
        );
        if (index >= 0) {
          const inStock = tixInStock[index];
          const atMaxForUser =
            numAllTicketsUserInGroups[index] >=
            visibleTickets[index].maxQuantity;
          selectedIsValid = inStock && !atMaxForUser;
        }
      }

      if (!selectedIsValid) {
        const firstAvailableTicket =
          visibleTickets[
            Math.max(
              tixInStock.findIndex((inStock) => inStock),
              visibleTickets.findIndex(
                (customTicket, index) =>
                  numVisibleTicketsUserInGroups[index] <
                  customTicket.maxQuantity
              )
            )
          ];
        if (firstAvailableTicket) {
          setSelectedCustomTickets([firstAvailableTicket]);
          setTicketQuantities(
            new Map([
              [firstAvailableTicket.id, firstAvailableTicket.minQuantity],
            ])
          );
          setNamedPrices(
            new Map([[firstAvailableTicket.id, firstAvailableTicket.price]])
          );
        }
      }

      setFirstAvailableTicketReady(true);
    }

    const allLabels = await Promise.all(
      visibleTickets.map(async (customTicket) => {
        const ticketsRemainingInGroup = await getTicketsRemainingInGroup(
          customTicket,
          event
        );
        const labels = await getTicketDescription(
          customTicket,
          ticketsRemainingInGroup,
          event.eventType
        );
        return labels;
      })
    );
    setDescriptions(allLabels);
  }, [
    firstAvailableTicketReady,
    event,
    numVisibleTicketsUserInGroups,
    setFirstAvailableTicketReady,
    setSelectedCustomTickets,
    visibleTickets,
    numTicketsRemainingInGroups,
    numAllTicketsUserInGroups,
    selectedCustomTickets,
  ]);

  const multiTicketPrompt = useCallback(
    (customTicket: CustomTicketV2, index: number) => {
      const reachedMaxTickets =
        numVisibleTicketsUserInGroups[index] >= customTicket.maxQuantity;

      return reachedMaxTickets ? (
        <span style={theme.LabelText} className="smallBodySubtext">
          *You have the max number of {itemLabel.plural}{" "}
          <span style={styles.blueBold}>
            ({numVisibleTicketsUserInGroups[index]})
          </span>{" "}
          for this type.
        </span>
      ) : numVisibleTicketsUserInGroups[index] > 0 ? (
        <span style={theme.LabelText} className="smallBodySubtext">
          *You have{" "}
          <span style={styles.blueBold}>
            {numVisibleTicketsUserInGroups[index]}
          </span>{" "}
          out of a maximum of{" "}
          <span style={styles.blueBold}>{customTicket.maxQuantity}</span> for
          this type.
        </span>
      ) : null;
    },
    [
      numVisibleTicketsUserInGroups,
      styles.blueBold,
      theme.LabelText,
      itemLabel.plural,
    ]
  );

  const rsvpToEvent = useCallback(async () => {
    const currentAnswers = answersRef.current;
    if (mixpanel) {
      mixpanel.track("Webapp: Attempting RSVP To Event", {
        event_id: event.id,
        event_type: ticketsPaid ? "paid" : "free",
        customTicketIds: foundCustomTicketIds,
        numberOfTickets: totalTicketQuantity,
        customer_phone: tempPhoneNumber,
        amount: totalAmountCents,
        form_responses: currentAnswers,
      });
    }

    // check if no ticket/order is selected, if so, then send alert
    if (isCustomTickets(event) && !selectedCustomTickets.length) {
      setSubmitted(false);
      alert(`Please select ${itemLabel.article} ${itemLabel.singular} option`);
      if (mixpanel) {
        mixpanel.track("Webapp: Attempting RSVP Failed, No Ticket Selected", {
          event_id: event.id,
          event_type: ticketsPaid ? "paid" : "free",
          customTicketIds: foundCustomTicketIds,
          numberOfTickets: totalTicketQuantity,
          customer_phone: tempPhoneNumber,
        });
      }
      return;
    }

    // check if ticket is redeem, promoter, or promo code, and has already been redeemed
    if (
      ticketData &&
      (ticketData.redeemedBy !== "" ||
        ticketData.scanned ||
        (ticketData.redeemedBy === "" &&
          ticketData.uid !== "" &&
          ticketData.uid === uid))
    ) {
      setSubmitted(false);
      alert(`This ${itemLabel.singular} has already been redeemed`);
      if (mixpanel) {
        mixpanel.track(
          "Webapp: Attempting RSVP Failed, Ticket Already Redeemed",
          {
            event_id: event.id,
            event_type: ticketsPaid ? "paid" : "free",
            customTicketIds: foundCustomTicketIds,
            numberOfTickets: totalTicketQuantity,
            customer_phone: tempPhoneNumber,
            form_responses: currentAnswers,
          }
        );
      }
      return;
    }

    // only pass the chargeId if the user paid for the ticket (and did not use free ticket code)
    const finalChargeId = discountedTicketPriceCents !== 0 ? chargeId : "";

    if (mixpanel) {
      mixpanel.track("Webapp: Calling Enqueue RSVP To Event", {
        event_id: event.id,
        event_type: ticketsPaid ? "paid" : "free",
        customTicketIds: foundCustomTicketIds,
        numberOfTickets: totalTicketQuantity,
        customer_phone: tempPhoneNumber,
        amount: totalAmountCents,
        form_responses: currentAnswers,
      });
    }

    // clear any answers that are not on the selected custom ticket so we only submit the answers that are on the selected custom ticket
    const filteredAnswers = Object.keys(currentAnswers).reduce(
      (acc: FormAnswersV2, questionId: string) => {
        if (
          formQuestionsToDisplay.find(
            (formQuestion) => formQuestion.id === questionId
          )
        ) {
          acc[questionId] = currentAnswers[questionId];
        }
        return acc;
      },
      createEmptyFormAnswersV2(formQuestionsToDisplay, [])
    );

    // in backend, make sure we don't set empty answers if they have data for that question already
    await API.rsvp
      .enqueueRSVPToEvent({
        toPhoneNumber: tempPhoneNumber,
        fullName: tempFullName,
        eventId: event.id,
        followSourceType: isDesktop
          ? FollowSourceType.DESKTOP_WEB
          : FollowSourceType.MOBILE_WEB,
        answers:
          formQuestionsToDisplay.length !== 0 ? filteredAnswers : undefined,
        customTicketIds: foundCustomTicketIds,
        numberOfTicketsByCustomTicketId: Object.fromEntries(
          ticketQuantities.entries()
        ), // Convert from map to record
        redeemTicketIds: currTixId ? [currTixId] : undefined,
        promoCodeId: currPromoCode ? currPromoCode.id : "",
        promoterId: promoterId ?? "",
        linkId: linkTrackerId,
        userReferrer: userReferrer,
        amount: discountedTicketPriceCents,
        tipAmount: tipAmountCents,
        chargeId: finalChargeId,
        customerId: customerId,
        uid: "",
      })
      .then(async (response) => {
        const { success, firstTicketIdTracker } = response;
        if (!success) {
          if (mixpanel) {
            mixpanel.track("Webapp: Failed RSVP To Event", {
              event_id: event.id,
              event_type: ticketsPaid ? "paid" : "free",
              customTicketIds: foundCustomTicketIds,
              numberOfTickets: totalTicketQuantity,
              customer_phone: tempPhoneNumber,
              error_message: "success returned false",
              form_responses: currentAnswers,
              failed_from_catch: false,
            });
          }

          // Redirect to confirmation page instead to show error
          redirectToConfirmation();
        } else {
          // listen for ticket creation before redirecting to confirmation page
          const ticketsRef = getTicketsRef(event.id);
          const ticketIdTrackerQuery = query(
            ticketsRef,
            where("id", "==", firstTicketIdTracker)
          );

          // redirect with an error if not redirected within 20 seconds
          const timeout = setTimeout(async () => {
            // check one more time if the ticket exists
            const ticketsRef = getTicketsRef(event.id);
            const ticketIdTrackerQuery = query(
              ticketsRef,
              where("id", "==", firstTicketIdTracker)
            );
            const ticketIdTrackerSnapshot = await getDocs(ticketIdTrackerQuery);
            if (!ticketIdTrackerSnapshot.empty) {
              const newTicket = ticketIdTrackerSnapshot.docs[0].data();

              // only redirect if the ticket is owned by the current user
              // TODO (jonathan): weird case exists where if the user isn't logged in, it could take them to the original ticket owner's confirmation page
              // This would only happen if the got defaulted to Markit verification code and not firebase
              if (
                newTicket.uid &&
                (accountData.uid === "" || newTicket.uid === accountData.uid)
              ) {
                redirectToConfirmation(newTicket.uid);
              } else {
                redirectToConfirmation();
              }
            } else {
              if (mixpanel) {
                mixpanel.track("Webapp: RSVP Processing Timeout", {
                  event_id: event.id,
                  event_type: ticketsPaid ? "paid" : "free",
                  customTicketIds: foundCustomTicketIds,
                  numberOfTickets: totalTicketQuantity,
                  customer_phone: tempPhoneNumber,
                });
              }

              redirectToConfirmation();
            }
          }, 20000);

          const unsubscribe = onSnapshot(ticketIdTrackerQuery, (snapshot) => {
            snapshot.docChanges().forEach(async (change) => {
              const newTicket = change.doc.data();

              // Only redirect when the ticket is owned by the current user
              // TODO (jonathan): weird case exists where if the user isn't logged in, it could take them to the original ticket owner's confirmation page
              // This would only happen if the got defaulted to Markit verification code and not firebase
              if (
                newTicket.uid &&
                (accountData.uid === "" || newTicket.uid === accountData.uid)
              ) {
                // make sure we don't redirect again since it found the ticket
                clearTimeout(timeout);

                redirectToConfirmation(newTicket.uid);
              }
            });
          });
          unsubscribeRef.current = unsubscribe;
        }
      })
      .catch((e: any) => {
        if (mixpanel) {
          mixpanel.track("Webapp: Failed RSVP To Event", {
            event_id: event.id,
            event_type: ticketsPaid ? "paid" : "free",
            customTicketIds: foundCustomTicketIds,
            numberOfTickets: totalTicketQuantity,
            fullName: tempFullName,
            customer_phone: tempPhoneNumber,
            error_message: e.message,
            form_responses: currentAnswers,
            failed_from_catch: true,
            toPhoneNumber: tempPhoneNumber,
            followSourceType: isDesktop
              ? FollowSourceType.DESKTOP_WEB
              : FollowSourceType.MOBILE_WEB,
            answers:
              formQuestionsToDisplay.length !== 0 ? currentAnswers : undefined,
            redeemTicketIds: [currTixId],
            promoCodeId: currPromoCode ? currPromoCode.id : "",
            promoterId: promoterId ?? "",
            linkId: linkTrackerId,
            userReferrer: userReferrer,
            amount: discountedTicketPriceCents,
            chargeId: finalChargeId,
            customerId: customerId,
            uid: "",
          });
        }

        // Redirect to confirmation page instead to show error
        redirectToConfirmation();
      });
  }, [
    mixpanel,
    event,
    uid,
    accountData.uid,
    selectedCustomTickets.length,
    ticketData,
    discountedTicketPriceCents,
    chargeId,
    formQuestionsToDisplay,
    tempPhoneNumber,
    tempFullName,
    foundCustomTicketIds,
    ticketQuantities,
    currTixId,
    currPromoCode,
    promoterId,
    linkTrackerId,
    userReferrer,
    customerId,
    ticketsPaid,
    totalTicketQuantity,
    totalAmountCents,
    tipAmountCents,
    itemLabel.article,
    itemLabel.singular,
    redirectToConfirmation,
  ]);

  const notYetRedeemed = useMemo(
    () => ticketData && ticketData.redeemedBy === "" && !ticketData.scanned,
    [ticketData]
  );

  // Determines if the number of tickets that the user is going to get exceeds the event capacity
  // false if you are redeeming a valid ticket too and also if you are redeeming a free ticket promo code
  const willExceedCapacity = useMemo(() => {
    if (
      !selectedCustomTickets.length ||
      !numTicketsRemainingInGroups.length ||
      notYetRedeemed ||
      isRedeemFreePromoCode
    ) {
      return false;
    }

    return selectedCustomTickets.some((ticket) => {
      if (ticket.quantityAvailable === 0) {
        return false;
      }
      const index = visibleTickets.findIndex(
        (visibleTicket) => visibleTicket.id === ticket.id
      );
      if (index < 0) {
        return false;
      }
      const remainingInGroup = numTicketsRemainingInGroups[index];
      const requestedQuantity = ticketQuantities.get(ticket.id) ?? 0;

      return requestedQuantity > remainingInGroup;
    });
  }, [
    isRedeemFreePromoCode,
    notYetRedeemed,
    numTicketsRemainingInGroups,
    selectedCustomTickets,
    ticketQuantities,
    visibleTickets,
  ]);

  // Determines if the number of tickets that the user is going to get exceeds the maximum quantity a user
  // can get of a specific ticket type
  const willExceedUserCapacity = useMemo(() => {
    if (
      !selectedCustomTickets.length ||
      !numVisibleTicketsUserInGroups.length
    ) {
      return false;
    }

    return selectedCustomTickets.some((ticket) => {
      const index = visibleTickets.findIndex(
        (visibleTicket) => visibleTicket.id === ticket.id
      );
      if (index < 0) {
        return false;
      }
      return numVisibleTicketsUserInGroups[index] >= ticket.maxQuantity;
    });
  }, [numVisibleTicketsUserInGroups, selectedCustomTickets, visibleTickets]);

  // Promo codes are per-checkout session, so will always increment usedCount by 1 regardless of the number of tickets being purchased
  const willExceedPromoCapacity = useMemo(
    () =>
      currPromoCode && currPromoCode.usedCount + 1 > currPromoCode.maxUseCount,
    [currPromoCode]
  );

  const exceedsCapacityLimits = useMemo(
    () =>
      willExceedUserCapacity || willExceedCapacity || willExceedPromoCapacity,
    [willExceedUserCapacity, willExceedCapacity, willExceedPromoCapacity]
  );

  const inputFormErrorPrompt = useMemo(
    () =>
      !paymentSheetLoading && firstAvailableTicketReady
        ? isOrganizer
          ? `You are an organizer for the ${itemLabel.entity} and cannot get ${itemLabel.article} ${itemLabel.singular}.`
          : willExceedCapacity && !isEventAtCapacity
            ? `The number of ${itemLabel.plural} you are trying to purchase exceeds the ${itemLabel.entity}'s capacity.`
            : willExceedUserCapacity && !isEventAtCapacity
              ? `You already have the max ${
                  itemLabel.singular
                } quantity allowed for ${
                  visibleTickets.length > 1
                    ? `each ${itemLabel.singular} type for `
                    : ""
                }this ${itemLabel.entity}.`
              : willExceedPromoCapacity && !isEventAtCapacity
                ? `The number of ${itemLabel.plural} you are trying to purchase exceeds the max use count for this promo code.`
                : undefined
        : undefined,
    [
      paymentSheetLoading,
      firstAvailableTicketReady,
      isOrganizer,
      willExceedCapacity,
      isEventAtCapacity,
      willExceedUserCapacity,
      visibleTickets.length,
      willExceedPromoCapacity,
      itemLabel,
    ]
  );

  // Determines if the checkout button should be disabled
  const checkoutBtnDisabled = useMemo(
    () =>
      submitted ||
      (!isRedeemFreePromoCode && !currTixId && exceedsCapacityLimits) ||
      isDisabledInputSection,
    [
      currTixId,
      isDisabledInputSection,
      isRedeemFreePromoCode,
      submitted,
      exceedsCapacityLimits,
    ]
  );

  const totalUserTickets = useMemo(() => {
    return numAllTicketsUserInGroups.reduce((sum, element) => sum + element, 0);
  }, [numAllTicketsUserInGroups]);

  // Determines if the questions section should be shown
  const showQuestionsSection = useMemo(
    () => formQuestionsToDisplay.length > 0,
    [formQuestionsToDisplay]
  );

  // Determines if the payment section should be shown. Don't show when the only paid tickets
  // are flexible pricing with $0 input (e.g. no min price and user entered zero).
  const showPaymentSection = useMemo(
    () =>
      selectedCustomTickets.some(
        (ticket) =>
          isPaidTicketType(ticket) &&
          getEffectivePriceCents(ticket, namedPrices) > 0
      ),
    [selectedCustomTickets, namedPrices]
  );

  // Tipping only applies to a real paid checkout, not a free ticket redemption
  const showTipSection = useMemo(
    () => event.tippingEnabled && showPaymentSection && !currTixId,
    [event.tippingEnabled, showPaymentSection, currTixId]
  );

  const showPurchaseError = useCallback(async () => {
    if (willExceedUserCapacity) {
      alert(
        `You have reached the maximum number of ${itemLabel.plural} for this selected ${itemLabel.singular} option. `
      );
      return true;
    }

    if (willExceedPromoCapacity) {
      alert(
        `The number of ${itemLabel.plural} you are trying to purchase exceeds the max use count for this promo code `
      );
      return true;
    }

    // prevent host from getting ticket/order
    if (event.createdBy === uid || event.createdBy === accountData.uid) {
      alert(
        `You are the host of this ${itemLabel.entity} and cannot get ${itemLabel.article} ${itemLabel.singular}.`
      );
      return true;
    }

    const errorIds = validateEventForm(answers, formQuestionsToDisplay);
    if (errorIds.length > 0) {
      setFormErrorIds(errorIds);
      // TODO (jonathan): Can't use alert container here because it doesn't work with the overlap of the modal
      alert("Please answer the required questions.");
      return true;
    }

    try {
      await finalizeFormFileUploadAnswers({
        creatorUid: event.createdBy,
        answers: answers,
        formQuestions: formQuestionsToDisplay,
        fileNamesByQuestionId: formFileUploads,
        setAnswers,
        answersRef,
        eventId: event.id,
        followerUid: uid,
      });
    } catch (e: any) {
      setSubmitted(false);
      setProcessing(false);
      alert("Failed to upload a file. Please try again.");
      return true;
    }

    return false;
  }, [
    willExceedUserCapacity,
    willExceedPromoCapacity,
    answers,
    formQuestionsToDisplay,
    event.createdBy,
    event.id,
    uid,
    accountData.uid,
    itemLabel.plural,
    itemLabel.singular,
    itemLabel.entity,
    itemLabel.article,
    formFileUploads,
  ]);

  // For Credit Card Payments
  const paymentConfirmedOnPress = useCallback(
    async (stripe: Stripe, elements: StripeElements) => {
      const purchaseError = await showPurchaseError();
      if (purchaseError) {
        return;
      }

      if (mixpanel) {
        mixpanel.track("Webapp: Attempting Ticket Purchase", {
          event_id: event.id,
          event_type: ticketsPaid ? "paid" : "free",
          customTicketIds: foundCustomTicketIds,
          numberOfTickets: totalTicketQuantity,
          customer_phone: tempPhoneNumber,
          apple_pay: false,
          amount: totalAmountCents,
          form_answers: answers,
        });
      }

      setProcessing(true);

      // Wrap Stripe's confirmPayment in a Promise for use in Promise.race
      const paymentPromise = new Promise((resolve, reject) => {
        stripe
          .confirmPayment({
            // `Elements` instance that was used to create the Payment Element
            elements,
            confirmParams: {
              return_url: "https://markitsocial.net/events/?id=" + event.id,
            },
            redirect: "if_required",
          })
          .then((result) => {
            if (result.error) {
              resolve(new Error(result.error.message));
            } else {
              resolve(result);
            }
          })
          .catch((error) => {
            resolve(new Error(error));
          });
      });

      // Set payment timeout of 30 seconds
      const paymentTimeout = 30000;
      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(new Error("Payment processing timed out. Please try again."));
        }, paymentTimeout);
      });

      const result = await Promise.race([paymentPromise, timeoutPromise]);

      if (result instanceof Error) {
        if (
          result.message === "Payment processing timed out. Please try again."
        ) {
          await paymentTimeoutHandler(
            event.id,
            tempPhoneNumber,
            "",
            totalAmountCents,
            totalTicketQuantity,
            ticketsPaid ? "paid" : "free",
            foundCustomTicketIds,
            tempPhoneNumber,
            mixpanel
          );

          alert(
            "Your payment has timed out. Please try again. This page will be refreshed."
          );
          window.location.reload();
        } else {
          // This point will only be reached if there is an immediate error when
          // confirming the payment. Show error to your customer (for example, payment
          // details incomplete)
          alert(
            "An error occurred with your payment method: " + result.message
          );
          mixpanel.track("Webapp: Error Buying a Ticket", {
            event_id: event.id,
            event_type: ticketsPaid ? "paid" : "free",
            error_message: result.message,
            customTicketIds: foundCustomTicketIds,
            numberOfTickets: totalTicketQuantity,
            customer_phone: tempPhoneNumber,
          });
          setProcessing(false);
        }
      } else {
        if (mixpanel) {
          mixpanel.track("Webapp: Bought a Ticket", {
            event_id: event.id,
            event_type: ticketsPaid ? "paid" : "free",
            customTicketIds: foundCustomTicketIds,
            numberOfTickets: totalTicketQuantity,
            amount: totalAmountCents,
            customer_phone: tempPhoneNumber,
            apple_pay: false,
          });
        }
        await rsvpToEvent();
      }
    },
    [
      answers,
      event.id,
      foundCustomTicketIds,
      mixpanel,
      rsvpToEvent,
      showPurchaseError,
      tempPhoneNumber,
      totalTicketQuantity,
      ticketsPaid,
      totalAmountCents,
    ]
  );

  // Displays the checkout button for the user to get the ticket
  const paymentOrRsvpElement = useMemo(() => {
    // This is a payment checkout if it's not a free ticket case and it's a paid ticket type and the amount is greater than 0
    const isPaymentOption =
      currTixId === "" &&
      !isRedeemFreePromoCode &&
      showPaymentSection &&
      discountedTicketPriceCents > 0;
    if (isPaymentOption) {
      if (options !== undefined) {
        return !inputFormErrorPrompt ? (
          <div
            style={{
              pointerEvents: exceedsCapacityLimits ? "none" : "all",
              opacity: exceedsCapacityLimits ? 0.3 : 1,
            }}
          >
            <Elements
              key={options.clientSecret}
              stripe={getStripePromise(host.uid)}
              options={options}
            >
              <CheckoutForm
                customerPhone={tempPhoneNumber}
                totalAmount={totalAmountCents}
                customTicketIds={foundCustomTicketIds}
                processing={processing}
                setProcessing={setProcessing}
                isReadyForPayment={
                  verificationState === VerificationState.VERIFIED
                }
                showPurchaseError={showPurchaseError}
                clientSecret={options.clientSecret}
                event={event}
                onPress={(stripe: Stripe, elements: StripeElements) =>
                  // For handling card payments
                  paymentConfirmedOnPress(stripe, elements)
                }
                rsvpToEvent={rsvpToEvent}
                ticketQuantity={totalTicketQuantity}
                disabled={
                  exceedsCapacityLimits ||
                  paymentSheetLoading ||
                  discountedTicketPriceCents !==
                    debouncedDiscountedPriceCents ||
                  hasInvalidPriceInput
                }
              />
            </Elements>
          </div>
        ) : null;
      } else {
        return (
          <h3 className="LoadingText" style={theme.PrimaryText}>
            ...
          </h3>
        );
      }
    } else {
      return (
        <RectangleButton
          buttonLabel={
            <span>
              {currTixId !== "" ||
              isRedeemFreePromoCode ||
              (showPaymentSection && currPromoCode)
                ? "Redeem"
                : isProductCheckout
                  ? "Order"
                  : selectedCustomTickets.some(
                        (ticket) =>
                          ticket.type === TicketType.FREE ||
                          ticket.flexiblePricing
                      )
                    ? "RSVP" // If flexiblePricing, the input amount is 0 since this falses in the non-paid case
                    : "Request"}
            </span>
          }
          onPress={async () => {
            const purchaseError = await showPurchaseError();
            if (!purchaseError) {
              setSubmitted(true);
              await rsvpToEvent();
            }
          }}
          theme={theme}
          altPaddingVert={12}
          disabled={
            checkoutBtnDisabled ||
            submitted ||
            !!inputFormErrorPrompt ||
            hasInvalidPriceInput
          }
          loading={submitted || loadingTicketData}
        />
      );
    }
  }, [
    currTixId,
    isRedeemFreePromoCode,
    showPaymentSection,
    discountedTicketPriceCents,
    options,
    inputFormErrorPrompt,
    exceedsCapacityLimits,
    host.uid,
    tempPhoneNumber,
    totalAmountCents,
    foundCustomTicketIds,
    processing,
    verificationState,
    showPurchaseError,
    event,
    rsvpToEvent,
    totalTicketQuantity,
    paymentSheetLoading,
    debouncedDiscountedPriceCents,
    hasInvalidPriceInput,
    paymentConfirmedOnPress,
    theme,
    currPromoCode,
    isProductCheckout,
    selectedCustomTickets,
    checkoutBtnDisabled,
    submitted,
    loadingTicketData,
  ]);

  return (
    <div
      className="RowNormal"
      style={{ gap: 60, width: "100%", justifyContent: "center" }}
    >
      <div
        className="ColumnNormal"
        style={{
          pointerEvents: processing ? "none" : "all",
          ...styles.leftContainer,
        }}
      >
        <NameAndPhoneNumberInput
          host={host}
          verificationState={verificationState}
          setVerificationState={setVerificationState}
          tempFullName={tempFullName}
          setTempFullName={setTempFullName}
          tempPhoneNumber={tempPhoneNumber}
          setTempPhoneNumber={setTempPhoneNumber}
          setUid={setUid}
          processing={processing}
          setIsOrganizer={setIsOrganizer}
          event={event}
          optedOut={optedOut}
          setOptedOut={setOptedOut}
          optedOutCreator={optedOutCreator}
          setOptedOutCreator={setOptedOutCreator}
        />
        {isBlacklistedUser ? (
          <>
            <HorizontalDivider altMargin={0} eventTheme={event.theme} />
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="NameAndNumber" style={{ ...theme.PrimaryText }}>
                Attention!
              </span>
              <span style={{ ...theme.PrimaryText, fontSize: 14 }}>
                You are blacklisted from purchasing anymore because you have
                disputed a previous charge. Please contact support by visiting
                our Contact Us Page below.
              </span>
            </div>
          </>
        ) : (
          <>
            {totalUserTickets > 0 ? (
              <>
                <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                <FullEventExistingTicketInfo
                  event={event}
                  userId={uid}
                  totalUserTickets={totalUserTickets}
                  numTicketsUserInGroups={numAllTicketsUserInGroups}
                />
              </>
            ) : null}
            <HorizontalDivider altMargin={0} eventTheme={event.theme} />
            <FullEventTicketOptions
              event={event}
              isRedeemTicket={currTixId !== ""}
              ticketForRedemption={ticketForRedemption}
              setTicketForRedemption={setTicketForRedemption}
              promoCode={currPromoCode}
              tixId={currTixId}
              selectedCustomTickets={selectedCustomTickets}
              setSelectedCustomTickets={setSelectedCustomTickets}
              descriptions={descriptions}
              ticketsInStock={ticketsInStock}
              multiTicketPrompt={multiTicketPrompt}
              ticketQuantities={ticketQuantities}
              setTicketQuantities={setTicketQuantities}
              namedPrices={namedPrices}
              setNamedPrices={setNamedPrices}
              numTicketsUserInGroups={numVisibleTicketsUserInGroups}
              disabled={isDisabledInputSection}
              verified={verified}
              isRedeemFreePromoCode={isRedeemFreePromoCode}
              inputFormErrorPrompt={inputFormErrorPrompt}
              paymentOrRsvpElement={
                !showPaymentSection && formQuestionsToDisplay.length === 0
                  ? paymentOrRsvpElement
                  : undefined
              }
            />
            {showQuestionsSection || showPaymentSection ? (
              <div className="ColumnNormal">
                <ExpandableBox>
                  {showQuestionsSection ? (
                    <div className="ColumnNormal" style={{ gap: 24 }}>
                      <HorizontalDivider
                        altMargin={0}
                        eventTheme={event.theme}
                      />
                      <FormResponsesProvider
                        formFileUploads={formFileUploads}
                        setFormFileUploads={setFormFileUploads}
                        formErrorIds={formErrorIds}
                        setFormErrorIds={setFormErrorIds}
                      >
                        <FormQuestionsInput
                          formQuestions={formQuestionsToDisplay}
                          answers={answers}
                          setAnswers={setAnswers}
                          disabled={isDisabledInputSection}
                          verified={verified}
                          loading={loadingFilledResponses}
                          paymentOrRsvpElement={
                            !showPaymentSection
                              ? paymentOrRsvpElement
                              : undefined
                          }
                        />
                      </FormResponsesProvider>
                    </div>
                  ) : null}
                </ExpandableBox>
                {/* Payment section only shows if tickets are paid and either 1) the user is not verified or 2) the user is verified and the currently selected ticket type is paid */}
                <ExpandableBox>
                  {showPaymentSection ? (
                    <div
                      className="ColumnNormal"
                      style={{
                        gap: 24,
                        paddingTop: showQuestionsSection ? 24 : 0,
                      }}
                    >
                      <HorizontalDivider
                        altMargin={0}
                        eventTheme={event.theme}
                      />
                      <FullEventPayment
                        event={event}
                        host={host}
                        ticketForRedemption={ticketForRedemption}
                        setTicketForRedemption={setTicketForRedemption}
                        promoCode={currPromoCode}
                        tixId={currTixId}
                        selectedCustomTickets={selectedCustomTickets}
                        setSelectedCustomTickets={setSelectedCustomTickets}
                        ticketQuantities={ticketQuantities}
                        setTicketQuantities={setTicketQuantities}
                        namedPrices={namedPrices}
                        baseTicketPriceCents={baseTicketPriceCents}
                        ticketFeesCents={ticketFeesCents}
                        totalAmountCents={totalAmountCents}
                        discountedTicketPriceCents={discountedTicketPriceCents}
                        numTicketsUserInGroups={numVisibleTicketsUserInGroups}
                        disabled={isDisabledInputSection}
                        verified={verified}
                        paymentProcessing={processing}
                        updatePromoCode={updatePromoCode}
                        updateTixId={updateTixId}
                        setIsRedeemFreePromoCode={setIsRedeemFreePromoCode}
                        paymentOrRsvpElement={paymentOrRsvpElement}
                        isDesktop={isDesktopSize}
                        showTipSection={showTipSection}
                        tipAmountCents={tipAmountCents}
                        setTipAmountCents={setTipAmountCents}
                      />
                    </div>
                  ) : null}
                </ExpandableBox>
              </div>
            ) : null}
          </>
        )}
        {ticketsPaid ? (
          <>
            <HorizontalDivider altMargin={0} eventTheme={event.theme} />
            <div style={{ gap: 14 }} className="ColumnNormal">
              <span className="NameAndNumber" style={{ ...theme.PrimaryText }}>
                Need Help?
              </span>
              <RectangleButton
                buttonLabel="Contact Support"
                onPress={contactUsOnPress}
                altPaddingVert={12}
                altTextColor={theme.PrimaryText.color}
                altColor={theme.NewPrimaryBG.backgroundColor}
                containerStyles={{
                  border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                }}
              />
            </div>
          </>
        ) : null}
      </div>
      {isDesktopSize ? (
        <StandardBorderedContainer
          containerStyles={{
            ...styles.rightContainer,
            ...theme.NewPrimaryBG,
          }}
        >
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <span className="sectionTitle" style={theme.PrimaryText}>
              {isProductCheckout
                ? "Order Summary"
                : ticketsPaid
                  ? "Ticket Summary"
                  : isRequestTickets(event)
                    ? "Request"
                    : "RSVP"}
            </span>
            <HorizontalDivider altMargin={0} eventTheme={event.theme} />
            <FullEventPaymentSummary
              event={event}
              host={host}
              ticketForRedemption={ticketForRedemption}
              setTicketForRedemption={setTicketForRedemption}
              selectedCustomTickets={selectedCustomTickets}
              setSelectedCustomTickets={setSelectedCustomTickets}
              promoCode={currPromoCode}
              tixId={currTixId}
              ticketQuantities={ticketQuantities}
              setTicketQuantities={setTicketQuantities}
              namedPrices={namedPrices}
              baseTicketPriceCents={baseTicketPriceCents}
              ticketFeesCents={ticketFeesCents}
              totalAmountCents={totalAmountCents}
              discountedTicketPriceCents={discountedTicketPriceCents}
              numTicketsUserInGroups={numVisibleTicketsUserInGroups}
              disabled={isDisabledInputSection}
              updatePromoCode={updatePromoCode}
              updateTixId={updateTixId}
              setIsRedeemFreePromoCode={setIsRedeemFreePromoCode}
              showTipSection={showTipSection}
              tipAmountCents={tipAmountCents}
              setTipAmountCents={setTipAmountCents}
            />
          </div>
        </StandardBorderedContainer>
      ) : null}
    </div>
  );
});
